import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import 'dayjs/locale/en-gb';
import { TIMEOUT_ERROR } from '@lib/constants/fetch';
import { createDebugAwareTracesSampler } from '@lib/services/logger';
import i18next from '@lib/locales/locales';
import '@lib/assets/styles/reset.css';
import App from './routes';
import reportWebVitals from './reportWebVitals';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);
dayjs.locale('en-gb');

const {
  MODE,
  VITE_API_URL,
  VITE_RELEASE_VERSION,
  VITE_SENTRY_DSN,
  VITE_SENTRY_TOKEN,
  VITE_SYSTEM_PLATFORM_URL,
  // @ts-ignore
} = import.meta.env;
const PLATFORM_URL = VITE_SYSTEM_PLATFORM_URL as string;
const API_URL = VITE_API_URL as string;

if (MODE !== 'development') {
  Sentry.init({
    dsn: VITE_SENTRY_DSN,
    release: VITE_RELEASE_VERSION,
    _experiments: {
      enableLogs: true,
    },
    integrations: [
      // Sentry.reactRouterV6BrowserTracingIntegration(),
      // Disable automatic console breadcrumb capture to avoid duplicates
      // (LoggerService handles sending logs to Sentry directly)
      Sentry.breadcrumbsIntegration({ console: false }),
      Sentry.replayIntegration(),
    ],
    ignoreErrors: [
      'Failed to fetch',
      'TypeError: Failed to fetch',
      'TypeError: NetworkError when attempting to fetch resource.',
      'Technical pauses fetch returned empty data',
      'signal is aborted without reason',
      'Blob loading failed',
      TIMEOUT_ERROR,
    ],
    beforeSend(event, hint) {
      const originalError = hint.originalException as
        | (Error & { graphQLErrors?: { extensions?: { code?: string } }[] })
        | undefined;
      if (
        originalError?.graphQLErrors?.some(
          (e) => e.extensions?.code === 'TECHNICAL_PAUSE_ERROR',
        )
      ) {
        return null;
      }
      if (event.exception) {
        const errorMessage = event.exception?.values?.[0]?.value;
        const errorFile =
          event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename;
        event.contexts = {
          ...event.contexts,
          customContext: {
            errorMessage,
            errorFile,
          },
        };
        event.fingerprint = ['{{ default }}', errorMessage, errorFile];
        hint.originalException = event.exception;
      }
      event.extra = {
        ...event.extra,
        'X-Sentry-Token': VITE_SENTRY_TOKEN,
        hint: JSON.stringify(hint),
      };
      return event;
    },
    tracesSampler: createDebugAwareTracesSampler(0.3),
    tracePropagationTargets: [PLATFORM_URL, API_URL],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <I18nextProvider i18n={i18next}>
      <App />
    </I18nextProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
